/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { type IconProps } from '@coursera/cds-common';
import { IconButton, type IconButtonProps, NotificationBadge, type NotificationBadgeProps } from '@coursera/cds-core';
import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';

import AnimatedAIGenerateBrandedIcon from 'bundles/ai-coach-platform/components/branding/AnimatedAIGenerateBrandedIcon';
import CoachIcon from 'bundles/ai-coach-platform/components/branding/CoachIcon';
import { isCoachBrandUpdateEnabled } from 'bundles/ai-coach-platform/utils/epicFeatureFlags';

import _t from 'i18n!nls/ai-coach-platform';

const COURSERA_AI_SPARKLE_ANIMATION_SESSION_KEY = 'courseraAISparkleAnimationShown';
// Matches AnimatedAIGenerateBrandedIcon's default durationMs (3000) + SMALL_SPARKLE_DELAY_MS (1000).
const SPARKLE_ANIMATION_DURATION_MS = 4000;

enum IconState {
  DEFAULT = 'default',
  WINK = 'wink',
  DISABLED = 'disabled',
}

type Props = {
  /**
   * Callback when the launcher CTA is clicked
   */
  onClick?: (selected: boolean) => void;

  /**
   * whether CTA is selected by default
   *
   * @default false
   */
  defaultSelected?: boolean;

  /**
   * whether Coach is disabled on the page
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * size of the Coach icon
   *
   * @default medium
   */
  size?: IconButtonProps['size'];

  tooltip?: string;

  /**
   * Count of notifications to show badge for. Hidden when count is <= 0 or undefined.
   * The max is "9" above which it will render "9+".
   * Implementing team is responsible to update/unset the count based on business logic.
   *
   * @default 0
   */
  notificationCount?: NotificationBadgeProps['value'];

  /**
   * Opt-in to the brand update variant. The variant is enabled when this is true AND the
   * `enableBrandUpdate` EPIC flag is active. Replaces the Coach avatar with a sparkle icon
   * and updates the tooltip and aria-label to "Chat with Coursera AI".
   * @default false
   */
  enableBrandUpdate?: boolean;
};

type SparkleIconProps = {
  showAnimation: boolean;
  size: IconProps['size'];
  disabled?: boolean;
};

const styles = {
  iconButton: (isSelected: boolean) => css`
    ${isSelected &&
    `
      .cds-button-label {
        color: var(--cds-color-blue-600);
      }

      background-color: var(--cds-color-emphasis-primary-background-xweak);
      border-radius: var(--cds-border-radius-100);
    `}
  `,
  disabledSparkle: css`
    path {
      fill: var(--cds-color-neutral-disabled-strong);
    }
  `,
  notificationBadge: css`
    /* Coach theme overrides */
    .cds-notificationBadge-default {
      background: linear-gradient(
        89deg,
        var(--cds-color-emphasis-primary-content-default) -7.38%,
        var(--cds-color-blue-600) 124.79%
      );
    }
  `,
};

const SparkleIcon = ({ showAnimation, size, disabled }: SparkleIconProps) => {
  if (disabled) {
    return <AIGenerateBrandedNewIcon size={size} css={styles.disabledSparkle} />;
  }
  if (showAnimation) {
    return <AnimatedAIGenerateBrandedIcon size={size} />;
  }
  return <AIGenerateBrandedNewIcon size={size} />;
};

export const CoachChatLauncher = (props: Props): React.ReactElement => {
  const {
    onClick,
    disabled = false,
    size = 'medium',
    tooltip,
    defaultSelected = false,
    notificationCount = 0,
    enableBrandUpdate = false,
  } = props;

  const isBrandUpdateEnabled = enableBrandUpdate && isCoachBrandUpdateEnabled();

  // Initialized to false so server and client agree on initial state (no SSR hydration mismatch).
  // The effect runs client-only after hydration to check and set the session key.
  const [showSparkleAnimation, setShowSparkleAnimation] = useState(false);

  useEffect(() => {
    if (!isBrandUpdateEnabled || disabled) return undefined;
    if (sessionStorage.getItem(COURSERA_AI_SPARKLE_ANIMATION_SESSION_KEY) !== 'true') {
      setShowSparkleAnimation(true);
      // Defer the write so premature mounts (e.g. the plugin-loading cycle on item pages)
      // that unmount before the animation completes don't block future visible mounts.
      const timeoutId = setTimeout(() => {
        sessionStorage.setItem(COURSERA_AI_SPARKLE_ANIMATION_SESSION_KEY, 'true');
      }, SPARKLE_ANIMATION_DURATION_MS);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const [coachIconState, setCoachIconState] = useState<IconState>(IconState.DEFAULT);
  const [selected, setSelected] = useState(defaultSelected);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const currentIconState = disabled ? IconState.DISABLED : coachIconState;

  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  useEffect(() => {
    const handleChatClose = () => {
      // [ACCESS-4084] when chat is closed with Chat close button,
      // set focus back to triggering launcher button
      containerRef.current?.focus();
    };

    if (!disabled) {
      // listen to custom event when Chat close button is clicked
      document.body.addEventListener('coachChatClosed', handleChatClose);
    }

    return () => {
      document.body.removeEventListener('coachChatClosed', handleChatClose);
    };
  }, [disabled]);

  const handleMouseEnter = useCallback<() => void>(() => {
    setCoachIconState(IconState.WINK);
  }, []);
  const handleFocus = useCallback<() => void>(() => {
    setCoachIconState(IconState.WINK);
  }, []);

  const handleMouseLeave = useCallback<() => void>(() => {
    setCoachIconState(IconState.DEFAULT);
  }, []);
  const handleBlur = useCallback<() => void>(() => {
    setCoachIconState(IconState.DEFAULT);
  }, []);

  const handleClick = () => {
    setSelected(!selected);
    onClick?.(!selected);
  };

  let tooltipText: string;
  if (isBrandUpdateEnabled) {
    tooltipText = disabled ? _t('Coursera AI Chat is not available on this page.') : _t('Chat with Coursera AI');
  } else {
    tooltipText = disabled
      ? _t('Coursera #{coachLabel} is not available on this page.', { coachLabel: 'Coach' })
      : _t('Chat with Coursera #{coachLabel}', { coachLabel: 'Coach' });
  }

  const ariaLabel = isBrandUpdateEnabled
    ? _t('Chat with Coursera AI')
    : _t('Chat with #{coachLabel}, your AI-powered learning assistant', { coachLabel: 'Coach' });

  return (
    <div ref={containerRef} tabIndex={-1} data-testid="coach-chat-launcher-container">
      <IconButton
        onMouseEnter={disabled ? undefined : handleMouseEnter}
        onMouseLeave={disabled ? undefined : handleMouseLeave}
        onFocus={disabled ? undefined : handleFocus}
        onBlur={disabled ? undefined : handleBlur}
        onClick={disabled ? undefined : handleClick}
        variant="ghost"
        aria-disabled={disabled} // `aria-disabled` allows users to view tooltip while in disabled state
        icon={
          <NotificationBadge value={notificationCount} align="top" css={styles.notificationBadge} max={9}>
            {isBrandUpdateEnabled ? (
              <SparkleIcon showAnimation={showSparkleAnimation} size="large" disabled={disabled} />
            ) : (
              <CoachIcon state={currentIconState} size={size} />
            )}
          </NotificationBadge>
        }
        tooltip={tooltip ?? tooltipText}
        aria-label={tooltip ?? ariaLabel}
        aria-expanded={selected}
        data-testid="coach-chat-launcher-button"
        // TODO: once enableBrandUpdate EPIC is cleaned up, remove this attribute and clean up the styles in ExternalCoachLauncherGridItem
        data-brand-update={isBrandUpdateEnabled}
        css={[styles.iconButton(selected)]}
      />
    </div>
  );
};

export default CoachChatLauncher;
